import { useCallback } from "react";

import { Button, ButtonSize } from "../button";
import { Badge, BadgeSize, BadgeVariant } from "../Badge";
import SvgGoogle from "../icons/Google";
import SvgGithubMark from "../icons/GithubMark";
import SvgGitlabLogo500Rgb from "../icons/GitlabLogo500Rgb";

import { SocialProvider } from "./constants";

import { useSocialAuthorize } from "@/queries/auth/useSocialAuthorize";
import { AUTH_CONFIG } from "@/constants";

interface SocialButtonProps {
  provider: SocialProvider;
  size?: ButtonSize;
}

const PROVIDER_LABELS: Record<SocialProvider, string> = {
  [SocialProvider.google]: "Google",
  [SocialProvider.gitlab]: "GitLab",
  [SocialProvider.github]: "GitHub",
};

const PROVIDER_ICONS: Record<SocialProvider, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  [SocialProvider.google]: SvgGoogle,
  [SocialProvider.gitlab]: SvgGitlabLogo500Rgb,
  [SocialProvider.github]: SvgGithubMark,
};

const PROVIDER_CONFIG: Record<
  SocialProvider,
  {
    backgroundColor: string;
    hoverColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  [SocialProvider.google]: {
    backgroundColor: "#FFFFFF",
    hoverColor: "#f8f9fa",
    textColor: "#1F1F1F",
    borderColor: "transparent",
  },
  [SocialProvider.gitlab]: {
    backgroundColor: "#FFFFFF",
    hoverColor: "#f8f9fa",
    textColor: "#1F1F1F",
    borderColor: "transparent",
  },
  [SocialProvider.github]: {
    backgroundColor: "#FFFFFF",
    hoverColor: "#f8f9fa",
    textColor: "#1F1F1F",
    borderColor: "transparent",
  },
};

export const SocialButton = ({ provider, size }: SocialButtonProps) => {
  const config = PROVIDER_CONFIG[provider];
  const IconComponent = PROVIDER_ICONS[provider];
  const label = PROVIDER_LABELS[provider];
  const lastLoginType = localStorage.getItem("last_login_type");
  const isCreateAccountPage = window.location.pathname === "/create-account";
  const { isOnPrem } = AUTH_CONFIG;

  const redirectUri =
    import.meta.env.NODE_ENV === "development"
      ? `http://127.0.0.1:3000/auth/callback`
      : `${window.location.origin}/auth/callback`;

  const { mutate, isLoading } = useSocialAuthorize({
    provider,
    redirect_uri: redirectUri,
  });

  const handleClick = useCallback(() => {
    mutate();
    localStorage.setItem("provider", provider);
  }, [mutate, provider]);

  const shouldShowLabel = size !== "tinyIcon" && size !== "icon" && size !== "tinyButton";

  return (
    <Button
      size={size || "xl"}
      className="relative p-5"
      variant="outline"
      onClick={handleClick}
      disabled={isLoading}
    >
      {lastLoginType === provider && !isOnPrem && !isCreateAccountPage && (
        <Badge
          variant={BadgeVariant.BLUE}
          size={BadgeSize.SM}
          className="absolute -right-3 -top-2.5 flex items-center gap-2 !text-xs shadow-sm"
        >
          Last used
        </Badge>
      )}
      <div className="flex items-center justify-center gap-3 px-4 py-2.5">
        {isLoading ? (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <IconComponent className="h-5 w-5 flex-shrink-0" aria-label={`${label} icon`} />
        )}
        {shouldShowLabel ? (
          <span
            className="text-sm font-medium"
            style={{
              color: config.textColor,
            }}
          >
            Continue with {label}
          </span>
        ) : null}
      </div>
    </Button>
  );
};
